import { useNavigate } from 'react-router-dom';
import { useSelectedLanguage } from '@/components/Dashboard/Common/Translate/atom';
import { ActionIcon, Divider, Group, Popover, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { searchQuery } from '@/graphql/query';
import { SearchIcons } from '@/components/Dashboard/ContextPages/Search/icons';
import { prefixPath } from '@/res/costants/utils';
import { useDebouncedValue } from '@mantine/hooks';
import { hasDivider } from '@/util/hasDivider';
import { accent } from '@/res/costants/colors';
import { Loading } from '../Loading/Loading';
import { useSmallerView } from '@/hooks/useSmallerView';
import { type SearchQuery } from '@/components/Dashboard/ContextPages/Search/Search';
import classes from './SearchBar.module.css';

export const SearchBar = () => {
  const navigate = useNavigate();
  const lang = useSelectedLanguage();
  const [searchValue, setSearchValue] = useState('');

  const [debouncedQuery] = useDebouncedValue(searchValue, 512);

  const { data, loading } = useQuery<SearchQuery>(searchQuery, {
    variables: { query: debouncedQuery, limit: 10, page: 1, types: [], lang },
    skip: !debouncedQuery,
    fetchPolicy: 'cache-and-network'
  });

  const actions = (data?.search?.items ?? []).map((item) => {
    const Icon = SearchIcons[item.type];
    return {
      label: item.title,
      value: item.id,
      type: item.type,
      description: item.id,
      group: t(`search.filtersLabel.${item.type}`),
      onTrigger: () => navigate(`${prefixPath(item.type)}/${item.id}`),
      icon: <Icon color={accent} size="1.2rem" />
    };
  });

  const openSearchPage = () => {
    if (searchValue) {
      navigate('/search/' + encodeURIComponent(searchValue) + '/1');
    }
  };

  const [show, setShow] = useState(false);

  const ref = useRef<HTMLInputElement>(null);

  const isMobile = useSmallerView('sm');

  const showPopup = show && searchValue;

  useEffect(() => {
    if (showPopup) {
      document.body.style.overflowY = 'clip';
    }
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [showPopup]);

  return (
    <>
      <Popover
        position="bottom"
        width={ref?.current?.offsetWidth ?? 0}
        offset={-2}
        opened={Boolean(showPopup)}
        zIndex={1007}>
        <Popover.Target>
          <TextInput
            classNames={{
              input: classes.searchInput
            }}
            data-tour="search-bar"
            variant="unstyled"
            ref={ref}
            radius="md"
            onFocus={() => setShow(true)}
            onBlur={() => setShow(false)}
            styles={{
              input: {
                padding: '0.5rem 1rem',
                transition: 'all 0.2s ease',
                background: showPopup ? 'white' : 'rgba(0,0,0,.15)',
                color: showPopup ? 'gray' : 'white',
                borderBottomLeftRadius: showPopup ? 0 : undefined,
                borderBottomRightRadius: showPopup ? 0 : undefined
              }
            }}
            style={{ maxWidth: isMobile ? '100%' : 400, flex: 1, viewTransitionName: 'searchbar' }}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                openSearchPage();
              }
            }}
            rightSection={
              <ActionIcon radius="xl" onClick={openSearchPage} color="gray">
                <IconSearch size="1.2rem" />
              </ActionIcon>
            }
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
            placeholder={t`Home.Search.placeholder`}
          />
        </Popover.Target>
        <Popover.Dropdown
          style={{ borderTop: 'none', borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
          p={0}>
          {loading ? (
            <Loading p="md" />
          ) : actions.length ? (
            actions.map((action, idx) => (
              <>
                <Stack p="md" gap={0} className={classes.searchItem} onClick={action.onTrigger}>
                  <Group wrap="nowrap">
                    {action.icon}
                    <Stack gap={0} flex={1}>
                      <Text size="sm" fw="bold">
                        {action.label}
                      </Text>
                      <Text inline size="xs" c="dimmed">
                        {action.description}
                      </Text>
                    </Stack>
                  </Group>
                </Stack>
                {hasDivider(idx, actions) && <Divider mx="md" />}
              </>
            ))
          ) : (
            <Text p="md" ta="center" size="sm" c="dimmed">{t`search.noResults`}</Text>
          )}
        </Popover.Dropdown>
      </Popover>
    </>
  );
};
